import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import { fontScale } from '@/theme';
import { mq, fluidHeight } from '@/theme/scalingTokens';

export const useHeroBannerStyles = makeStyles({
  root: {
    position: 'relative',
    width: 'auto',
    height: '200px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    ...shorthands.borderRadius('16px'),
    ...shorthands.overflow('hidden'),
    marginLeft: '96px',
    marginRight: '96px',
    marginTop: '24px',
    [mq.ultraWideUp]: {
      marginLeft: '5%',
      marginRight: '5%',
      height: fluidHeight.bannerSm,
    },
    [mq.wide]: {
      marginLeft: '48px',
      marginRight: '48px',
    },
    [mq.smallDesktop]: {
      marginLeft: '24px',
      marginRight: '24px',
      height: '180px',
    },
    [mq.tablet]: {
      height: '160px',
      marginLeft: '16px',
      marginRight: '16px',
      marginTop: '16px',
      ...shorthands.borderRadius('12px'),
    },
    [mq.mobile]: {
      height: '140px',
      marginLeft: '12px',
      marginRight: '12px',
      marginTop: '12px',
      ...shorthands.borderRadius('8px'),
    },
  },
  /**
   * Figma-aligned hero root: light tinted gradient canvas, soft border, no
   * background image. Lays out content (title block + actions) on the left
   * and an optional floating tip-card slot on the right.
   */
  rootFigma: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    width: 'auto',
    minHeight: '11.25rem',
    boxSizing: 'border-box',
    marginLeft: '48px',
    marginRight: '48px',
    marginTop: '24px',
    ...shorthands.borderRadius('0.5rem'),
    ...shorthands.overflow('hidden'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    boxShadow: tokens.shadow2,
    [mq.smallDesktop]: {
      marginLeft: '24px',
      marginRight: '24px',
    },
    [mq.tablet]: {
      marginLeft: '16px',
      marginRight: '16px',
      marginTop: '16px',
      minHeight: 'auto',
    },
    [mq.mobile]: {
      marginLeft: '12px',
      marginRight: '12px',
      marginTop: '12px',
    },
  },
  figmaImageLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    zIndex: 0,
    pointerEvents: 'none',
    opacity: 0.7,
  },
  figmaImageProbe: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    opacity: 0,
    pointerEvents: 'none',
  },
  /**
   * Transparent layer between the hero photo (figmaImageLayer, z0) and the
   * content (figmaInner, z1). The artwork carries its own contrast in both
   * modes, so this is a structural no-op kept for layout stacking.
   */
  figmaScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    zIndex: 0,
    pointerEvents: 'none',
  },
  figmaInner: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: '1 1 auto',
    minHeight: 'inherit',
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
    rowGap: tokens.spacingVerticalL,
    [mq.tablet]: {
      ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    },
  },
  /**
   * Home-screen alignment variant (spec 101 FR-001 / FR-002): extends the
   * inner horizontal padding so the title/description/CTA line up with the
   * "Your Booking Requests" section heading (which sits at 5% of viewport
   * via BookingsDashboard.contentArea). Banner card width is unchanged —
   * only the inner content is shifted. At narrow widths where 5% would land
   * inside the existing visual padding, the original padding wins (max).
   * Symmetric on the right so the tip card mirrors the left inset.
   */
  figmaInnerHomeAligned: {
    paddingLeft: `max(${tokens.spacingHorizontalXL}, calc(5% - 48px))`,
    paddingRight: `max(${tokens.spacingHorizontalXL}, calc(5% - 48px))`,
    [mq.smallDesktop]: {
      paddingLeft: `max(${tokens.spacingHorizontalXL}, calc(5% - 24px))`,
      paddingRight: `max(${tokens.spacingHorizontalXL}, calc(5% - 24px))`,
    },
    [mq.tablet]: {
      paddingLeft: `max(${tokens.spacingHorizontalL}, calc(24px - 16px))`,
      paddingRight: `max(${tokens.spacingHorizontalL}, calc(24px - 16px))`,
    },
    [mq.mobile]: {
      paddingLeft: `max(${tokens.spacingHorizontalL}, calc(16px - 12px))`,
      paddingRight: `max(${tokens.spacingHorizontalL}, calc(16px - 12px))`,
    },
  },
  figmaTopRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalXL,
    width: '100%',
    [mq.tablet]: {
      flexDirection: 'column',
      rowGap: tokens.spacingVerticalL,
    },
  },
  figmaContent: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    flex: '1 1 auto',
    minWidth: 0,
    maxWidth: '32rem',
  },
  figmaBackLink: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalXS,
  },
  figmaActions: {
    display: 'flex',
    flexDirection: 'row',
    columnGap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
    flexWrap: 'wrap',
  },
  figmaTipSlot: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'flex-start',
    [mq.tablet]: {
      width: '100%',
      justifyContent: 'flex-start',
    },
  },
  figmaStepperSlot: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginTop: 'auto',
  },
  eyebrow: {
    fontSize: fontScale.caption,
    fontWeight: '700',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: tokens.colorBrandStroke1,
    margin: 0,
  },
  titleFigma: {
    fontSize: fontScale.heading,
    fontWeight: '700',
    lineHeight: '1.15em',
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  subtitleFigma: {
    fontSize: fontScale.body,
    lineHeight: '1.5em',
    color: tokens.colorNeutralForeground2,
    margin: 0,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 50%, transparent 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    [mq.mobile]: {
      background:
        'linear-gradient(to right, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 70%, transparent 100%)',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    ...shorthands.padding('32px', '48px', '32px', '32px'),
    maxWidth: '500px',
    [mq.smallDesktop]: {
      ...shorthands.padding('24px', '32px', '24px', '24px'),
    },
    [mq.tablet]: {
      ...shorthands.padding('20px', '24px', '20px', '20px'),
      maxWidth: '100%',
    },
    [mq.mobile]: {
      ...shorthands.padding('16px', '16px', '16px', '16px'),
      ...shorthands.gap('4px'),
    },
  },
  title: {
    fontSize: fontScale.heading,
    fontWeight: '600',
    lineHeight: '1.1em',
    color: tokens.colorNeutralForegroundOnBrand,
    margin: 0,
  },
  subtitle: {
    fontSize: fontScale.body,
    lineHeight: '1.43em',
    fontWeight: '400',
    color: tokens.colorNeutralForegroundOnBrand,
    margin: 0,
    [mq.mobile]: {
      fontSize: fontScale.caption,
      lineHeight: '1.33em',
    },
  },
});
